/**
 * voice-v8 — Karte „Prozess bis zum Ende laufen lassen?“ (27.10.2026, Martins Knopf-Ja k-4d6ba4; Server: server/prozess-freigabe.ts).
 *
 * Erscheint, wenn ein Browser-Prozess an die Grenze kommt (früher: „Sag ‚weiter‘“). Ja/Nein über das gemeinsame Knopfpaar
 * (karten/knopf-paar.ts): der Knopf schickt nur den FESTEN Wortlaut mit der Kennung des Servers — kein neuer Schreibweg, kein Markup.
 * Die Freigabe setzt allein der Server, wenn der Wortlaut genau zur offenen Karte passt. Nur ein ECHTER Tipp sendet (knopf-paar.ts):
 * ein Skript-Klick der Chrome-Erweiterung auf die App schickt nichts.
 *
 * Mandat (27.10.2026, Autonomie-Modus B3): die Mandats-Karte VOR dem Start (Ergebnis von `mandat_anfragen`, vom Server gefüllt) — Art,
 * Martins eigener Auftrag, was immer eigener Knopf bleibt, drei Stufen-Knöpfe (20 Min / 1 Std / ganzer Tag mit Kostendeckel) und Nein.
 * Jeder Stufen-Knopf schickt seinen festen Wortlaut aus Stufe, Kennung und Kurzwert des Servers. Dazu die Halt-Karte eines angehaltenen
 * Mandats (Weiter / Beenden). Alles als Text (textContent), nichts aus dem Ergebnis wird Markup.
 */
import {
  istMandatArt,
  MANDAT_ART_TITEL,
  MANDAT_IMMER_EIGENER_KNOPF,
  MANDAT_KARTE,
  MANDAT_KURZWERT,
  MANDAT_STUFEN,
  MANDAT_STUFEN_WERTE,
  mandatKnopfJa,
  mandatKnopfNein,
  mandatMorgen,
  mandatWeiterJa,
  mandatWeiterNein,
  PROZESS_FRAGE,
  PROZESS_KARTE,
  PROZESS_KENNUNG,
  prozessKnopfJa,
  prozessKnopfNein,
  type MandatStufe,
} from "../../../shared/prozess-freigabe.js";
import { absatz, baueKarte, kartenTitel } from "../karte.js";
import { trimText, unwrapResult } from "./hilfe.js";
import { fristAus, haengeKnopfPaarAn, haengeKnopfReiheAn, type KnopfEintrag } from "./knopf-paar.js";

const TITEL = "Prozess bis zum Ende laufen lassen?";
const ABGELAUFEN_SATZ = "Diese Frage gilt nicht mehr. Sag „weiter“, dann frage ich neu.";
const HALT_TITEL = "Mandat angehalten";
const LIMIT_TITEL: Readonly<Record<string, string>> = Object.freeze({
  tageslimit: "Tageslimit erreicht",
  budget: "Kostendeckel erreicht",
  schritte: "Schritte aufgebraucht",
  "abo-fenster": "Abo-Kontingent fast voll",
  "abo-fenster-unbekannt": "Abo-Kontingent nicht ablesbar",
});
/** Abo-Weg: die Schritte eines Mandats hebt niemand an; das Abo-Fenster wird von selbst wieder frei. */
const SCHRITTE_SATZ = "Die Schritte eines Mandats hebt niemand an. Weiter geht es nur mit einem neuen Mandat.";
const FENSTER_SATZ = "Das Abo-Kontingent wird von selbst wieder frei. „Weiter“ prüft es neu – ich hebe nie etwas an.";
const LIMIT_VORSCHLAG_SATZ = "Der Betrag ist nur ein Vorschlag. Ein Limit hebst du selbst in den Einstellungen an – ich hebe nie eins an.";
/** Kostendeckel des Mandats (Prüfbefund Kann): er lässt sich nirgends anheben — darum kein „Weiter“ und kein Betrag. */
const DECKEL_SATZ = "Den Kostendeckel eines Mandats hebt niemand an. Weiter geht es nur mit einem neuen Mandat.";
const MANDAT_ABGELAUFEN_SATZ = "Diese Karte gilt nicht mehr. Gib den Auftrag neu, dann zeige ich eine neue.";

/** Ist dieses Ergebnis die Prozess-Karte (oder die Halt-Karte eines Mandats) mit gültiger Kennung? */
export function istProzessRueckfrage(tool: string, result: unknown): boolean {
  if (tool !== PROZESS_KARTE) return false;
  const r = unwrapResult(result);
  return r.wartet_auf_bestaetigung === true && typeof r.vorschlag_id === "string" && PROZESS_KENNUNG.test(r.vorschlag_id);
}

/** Die Karte — `null` ohne gültige Kennung. Die Frage steht nur als Text (textContent). */
export function buildProzessFreigabeCard(result: unknown): HTMLElement | null {
  const r = unwrapResult(result) as {
    vorschlag_id?: unknown;
    frage?: unknown;
    gueltig_bis_ms?: unknown;
    mandat_halt?: unknown;
    mandat_limit?: unknown;
    vorschlag_cent?: unknown;
    auftrag?: unknown;
  };
  const id = typeof r.vorschlag_id === "string" ? r.vorschlag_id : "";
  if (!PROZESS_KENNUNG.test(id)) return null;
  const halt = r.mandat_halt === true;
  const limit = halt && typeof r.mandat_limit === "string" ? LIMIT_TITEL[r.mandat_limit] : undefined;
  const el = baueKarte(halt ? "mandat" : "prozess");
  el.appendChild(kartenTitel(limit ?? (halt ? HALT_TITEL : TITEL)));
  const frage = typeof r.frage === "string" && r.frage.trim() ? trimText(r.frage.trim(), 300) : halt ? "" : PROZESS_FRAGE;
  if (frage) el.appendChild(absatz(frage));
  // Nach einem Server-Neustart: Martins Auftrag (sein Wortlaut, vom Server gegen den Lauf-Zettel geprüft) — nur als Text.
  const auftrag = halt && typeof r.auftrag === "string" ? trimText(r.auftrag.trim(), 400) : "";
  if (auftrag) {
    const q = document.createElement("blockquote");
    q.className = "mandat-auftrag";
    q.textContent = auftrag;
    el.appendChild(q);
  }
  if (limit) {
    // Limit-Karte (B6). Tageslimit: „+X €“ ist nur ein Vorschlag — der Server hebt nichts an, er prüft vor jeder Runde neu.
    // Kostendeckel: nur „Morgen“ und „Beenden“ (der Server nimmt dort auch kein „Weiter“ an).
    const deckel = r.mandat_limit === "budget" || r.mandat_limit === "schritte";
    const cent = !deckel && typeof r.vorschlag_cent === "number" && Number.isSafeInteger(r.vorschlag_cent) && r.vorschlag_cent > 0 ? r.vorschlag_cent : null;
    // Geschützte Leerzeichen: „Weiter (+8 €)“ bricht auf dem Handy nie mitten im Betrag um.
    const plus = cent ? `\u00a0(+${euro(cent).replace(" ", "\u00a0")})` : "";
    const regel = absatz(
      r.mandat_limit === "schritte" ? SCHRITTE_SATZ : deckel ? DECKEL_SATZ : r.mandat_limit === "abo-fenster" || r.mandat_limit === "abo-fenster-unbekannt" ? FENSTER_SATZ : LIMIT_VORSCHLAG_SATZ,
    );
    regel.classList.add("mandat-regel");
    el.appendChild(regel);
    const weiter: KnopfEintrag[] = deckel
      ? []
      : [{ text: `Weiter${plus}`, primaer: true, ja: true, nachricht: mandatWeiterJa(id), name: r.mandat_limit === "abo-fenster" || r.mandat_limit === "abo-fenster-unbekannt" ? "Weiter, wenn das Abo-Kontingent wieder Luft hat" : "Weiter, wenn du das Tageslimit selbst angehoben hast", gesendet: "Gesendet — ich mache weiter, wenn das Limit es zulässt." }];
    haengeKnopfReiheAn(el, {
      knoepfe: [
        ...weiter,
        { text: "Morgen weitermachen", ...(deckel ? { primaer: true } : {}), ja: false, nachricht: mandatMorgen(id), name: "Morgen weitermachen, mit neuem Tipp", gesendet: "Gesendet — morgen geht es mit einer neuen Karte weiter." },
        { text: "Beenden", ja: false, nachricht: mandatWeiterNein(id), name: "Mandat beenden", gesendet: "Gesendet — das Mandat ist beendet." },
      ],
      reiheKlasse: "mandat-stufen",
      bisMs: fristAus(r.gueltig_bis_ms),
      abgelaufenSatz: MANDAT_ABGELAUFEN_SATZ,
    });
    return el;
  }
  if (halt) {
    haengeKnopfReiheAn(el, {
      knoepfe: [
        { text: "Weiter", primaer: true, ja: true, nachricht: mandatWeiterJa(id), name: "Mandat fortsetzen", gesendet: "Gesendet — ich mache weiter." },
        { text: "Beenden", ja: false, nachricht: mandatWeiterNein(id), name: "Mandat beenden", gesendet: "Gesendet — das Mandat ist beendet." },
      ],
      bisMs: fristAus(r.gueltig_bis_ms),
      abgelaufenSatz: MANDAT_ABGELAUFEN_SATZ,
    });
    return el;
  }
  haengeKnopfPaarAn(el, {
    ja: { nachricht: prozessKnopfJa(id), name: "Ja, Prozess bis zum Ende laufen lassen", gesendet: "Gesendet — ich mache weiter." },
    nein: { nachricht: prozessKnopfNein(id), name: "Nein, Prozess nicht weiterlaufen lassen", gesendet: "Gesendet — der Prozess bleibt stehen." },
    bisMs: fristAus(r.gueltig_bis_ms),
    abgelaufenSatz: ABGELAUFEN_SATZ,
  });
  return el;
}

/** Die Kurzwerte je Stufe aus dem Ergebnis — `null`, wenn eine Stufe fehlt oder ihr Kurzwert nicht die erwartete Form hat. */
function kurzwerteAus(stufen: unknown): Record<MandatStufe, string> | null {
  if (!Array.isArray(stufen)) return null;
  const aus: Partial<Record<MandatStufe, string>> = {};
  for (const s of stufen as unknown[]) {
    const { stufe, kurzwert } = (s ?? {}) as { stufe?: unknown; kurzwert?: unknown };
    if (typeof stufe === "string" && (MANDAT_STUFEN as readonly string[]).includes(stufe) && typeof kurzwert === "string" && MANDAT_KURZWERT.test(kurzwert)) {
      aus[stufe as MandatStufe] = kurzwert;
    }
  }
  return MANDAT_STUFEN.every((s) => aus[s] !== undefined) ? (aus as Record<MandatStufe, string>) : null;
}

/** Ist dieses Ergebnis eine Mandats-Karte (vom Server gefüllt, Kennung und alle Kurzwerte gültig)? */
export function istMandatRueckfrage(tool: string, result: unknown): boolean {
  if (tool !== MANDAT_KARTE) return false;
  const r = unwrapResult(result);
  return (
    r.wartet_auf_bestaetigung === true &&
    typeof r.vorschlag_id === "string" &&
    PROZESS_KENNUNG.test(r.vorschlag_id) &&
    istMandatArt(r.art) &&
    kurzwerteAus(r.stufen) !== null
  );
}

/** Euro ohne Nachkommastellen, wenn glatt („3 €“), sonst mit Komma. */
function euro(cent: number): string {
  return `${cent % 100 === 0 ? String(cent / 100) : (cent / 100).toFixed(2).replace(".", ",")} €`;
}

/** Die Mandats-Karte — `null` ohne gültige Kennung, Art oder Kurzwerte. Alles als Text. */
export function buildMandatCard(result: unknown): HTMLElement | null {
  const r = unwrapResult(result) as { vorschlag_id?: unknown; art?: unknown; aufgabe?: unknown; stufen?: unknown; gueltig_bis_ms?: unknown };
  const id = typeof r.vorschlag_id === "string" ? r.vorschlag_id : "";
  const kurz = kurzwerteAus(r.stufen);
  if (!PROZESS_KENNUNG.test(id) || !istMandatArt(r.art) || !kurz) return null;
  const el = baueKarte("mandat");
  el.appendChild(kartenTitel(MANDAT_ART_TITEL[r.art], "Mandat"));
  const aufgabe = typeof r.aufgabe === "string" ? trimText(r.aufgabe.trim(), 600) : "";
  if (aufgabe) {
    const q = document.createElement("blockquote");
    q.className = "mandat-auftrag";
    q.textContent = aufgabe;
    el.appendChild(q);
  }
  el.appendChild(absatz("Wie lange darf ich daran arbeiten? Ich arbeite im Hintergrund, du kannst weiterreden. „Stopp“ beendet es sofort."));
  const knoepfe: KnopfEintrag[] = MANDAT_STUFEN.map((stufe) => {
    const w = MANDAT_STUFEN_WERTE[stufe];
    return {
      text: w.titel,
      unter: `bis ${euro(w.euroCent)}`,
      // Die kürzeste Stufe ist die Vorgabe (Martins Entscheidung: Standard 20 Min).
      ...(stufe === MANDAT_STUFEN[0] ? { primaer: true } : {}),
      ja: true,
      nachricht: mandatKnopfJa(stufe, id, kurz[stufe]),
      name: `Mandat ${w.titel}, Kosten bis ${euro(w.euroCent)}`,
      gesendet: `Gesendet — ich arbeite ${w.titel === "Ganzer Tag" ? "heute" : w.titel} daran.`,
    };
  });
  knoepfe.push({ text: "Nein", ja: false, nachricht: mandatKnopfNein(id), name: "Kein Mandat", gesendet: "Gesendet — kein Mandat." });
  const regel = absatz(`Immer dein eigener Knopf: ${MANDAT_IMMER_EIGENER_KNOPF}.`);
  regel.classList.add("mandat-regel");
  el.appendChild(regel);
  haengeKnopfReiheAn(el, {
    knoepfe,
    reiheKlasse: "mandat-stufen",
    bisMs: fristAus(r.gueltig_bis_ms),
    abgelaufenSatz: MANDAT_ABGELAUFEN_SATZ,
  });
  return el;
}
